import React from "react";
import { InfoIcon } from "lucide-react";

import {
  DropdownMenuItem as DropdownMenuItemUI,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../dropdown-menu";
import { BasicTooltip } from "../../Tooltip";
import { OverflowDisplayTooltip } from "../../OverflowDisplayTooltip";
import { cn } from "../../utils";

import { Action } from "./types";
import {
  dropdownMenuIconVariants,
  dropdownMenuItemVariants,
  dropdownMenuTextVariants,
} from "./variants";

interface DropdownMenuItemProps<DataT> {
  action: Action<DataT>;
  item?: DataT;
}

const getIcon = (Icon: React.ElementType | React.ReactNode) => {
  if (React.isValidElement(Icon)) {
    return Icon;
  }
  const Component = Icon as React.ElementType;
  return <Component className="h-4 w-4" />;
};

const renderActionContent = <DataT,>({
  Icon,
  label,
  variant,
  tooltipClassName,
  tooltipMessage,
  infoTooltipMessage,
  isDisabled,
}: {
  Icon: Action<DataT>["icon"];
  label?: string;
  variant?: Action<DataT>["variant"];
  tooltipClassName?: string;
  tooltipMessage?: string;
  infoTooltipMessage?: string;
  isDisabled: boolean;
}) => (
  <BasicTooltip shouldShow={Boolean(isDisabled && tooltipMessage)} content={tooltipMessage}>
    <span className="flex min-w-0 flex-1 items-center text-sm">
      {Icon ? (
        <span
          className={dropdownMenuIconVariants({
            variant: variant || "primary",
          })}
        >
          {getIcon(Icon)}
        </span>
      ) : null}
      {label ? (
        <OverflowDisplayTooltip className={`max-w-[150px] ${tooltipClassName ?? ""}`} content={label}>
          <span
            className={dropdownMenuTextVariants({
              variant: variant || "primary",
            })}
          >
            {label}
          </span>
        </OverflowDisplayTooltip>
      ) : null}
      {infoTooltipMessage ? (
        <BasicTooltip content={infoTooltipMessage}>
          <span
            className="ml-1 inline-flex shrink-0"
            onClick={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
          >
            <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" aria-hidden />
          </span>
        </BasicTooltip>
      ) : null}
    </span>
  </BasicTooltip>
);

export const DropdownMenuItem = <DataT,>({ action, item }: DropdownMenuItemProps<DataT>) => {
  const {
    id,
    label,
    icon: Icon,
    variant,
    className,
    getDisabled,
    onClick,
    isDivider,
    shouldShow,
    showIf,
    tooltipClassName,
    tooltipContent,
    infoTooltipContent,
    subItems,
  } = action;

  if (isDivider) {
    return <div key={id} className="my-1 h-px bg-gray-200" />;
  }

  if (shouldShow === false) {
    return null;
  }

  if (showIf && !showIf(item || undefined)) {
    return null;
  }

  const isDisabled = getDisabled?.(item || undefined) || false;
  const tooltipMessage =
    typeof tooltipContent === "function" ? tooltipContent(item || undefined) : tooltipContent;
  const infoTooltipMessage =
    typeof infoTooltipContent === "function"
      ? infoTooltipContent(item || undefined)
      : infoTooltipContent;

  const itemClassName = dropdownMenuItemVariants({
    variant: variant || "primary",
    className: cn(
      "group",
      isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
      className,
    ),
  });

  const content = renderActionContent({
    Icon,
    label,
    variant,
    tooltipClassName,
    tooltipMessage,
    infoTooltipMessage,
    isDisabled,
  });

  if (subItems?.length) {
    return (
      <DropdownMenuSub key={id}>
        <DropdownMenuSubTrigger disabled={isDisabled} className={itemClassName}>
          {content}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-56 p-1.5">
          {subItems.map(subItem => (
            <DropdownMenuItem key={subItem.id} action={subItem} item={item} />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenuItemUI
      key={id}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isDisabled) {
          onClick?.(item || undefined);
        }
      }}
      className={itemClassName}
    >
      {content}
    </DropdownMenuItemUI>
  );
};
